import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
//updating antd config from moment to dayjs
//antd uses moment js by default for reading value
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
//updated component that uses dayjs instead of moment
const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);
const { RangePicker } = DatePicker;
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { Button } from 'antd';
import { useOnClickOutside } from 'hooks/general/useOnOutsideClick';
import { getConfig } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { useContext } from 'react';
import { DisabledDatesType } from './Calender';
import { FilterContextData } from 'contexts/FilterContext';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

interface MonthlyCalenderRangeProps {
    tempRange: any;
    setTempRange: Function;
    onDateSubmit: (startDateUnix: number, endDateUnix: number) => void;
    calenderDatesConfig: {
        startYear?: number;
        disabledDates?: DisabledDatesType;
    };
    placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
    hideInput?: boolean;
    disabled?: boolean;
    singleSelect?: boolean;
}

interface MonthlyCalenderRangeRef {
    openPopup: () => void;
}

export type { MonthlyCalenderRangeRef };

const MonthlyCalenderRange = forwardRef<MonthlyCalenderRangeRef, MonthlyCalenderRangeProps>(
    (
        {
            tempRange,
            setTempRange,
            onDateSubmit,
            calenderDatesConfig,
            placement,
            hideInput = false,
            disabled,
            singleSelect = false,
        },
        ref,
    ) => {
        const pickerRef = useRef<any>(null);
        const [isOpen, setIsOpen] = useState(false);
        const { appConfig } = useContext(UserContext);
        const commonConfig = getConfig('commonConfig', appConfig);

        //close expanded filter on clicking outside
        const clickOutsideHandler = useCallback(
            (event: any) => {
                if (isOpen) {
                    setIsOpen(false);
                }
            },
            [isOpen],
        );
        useOnClickOutside(pickerRef, clickOutsideHandler);

        const handleMonthlyCalenderOpenChange = (open: boolean) => {
            if (!isOpen && open) {
                setIsOpen(open);
            }
        };

        const monthlyCalenderChange = (values: any, selectedDates: [string, string]) => {
            if (singleSelect) {
                if (selectedDates?.length && selectedDates?.[1]) {
                    const dayjsEndDate = dayjs(selectedDates[1], commonConfig?.dateFormat).startOf('month');
                    setTempRange([tempRange[0], dayjsEndDate]);
                }
            } else {
                if (selectedDates?.length && selectedDates?.[0] && selectedDates?.[1]) {
                    const dayjsStartDate = dayjs(selectedDates[0], commonConfig?.dateFormat).startOf('month');
                    const dayjsEndDate = selectedDates?.[1]
                        ? dayjs(selectedDates[1], commonConfig?.dateFormat).startOf('month')
                        : null;
                    setTempRange([dayjsStartDate, dayjsEndDate]);
                }
            }
        };

        //called when clicked on set date for monthly calender
        const onMonthlySubmit = () => {
            if (singleSelect) {
                if (tempRange?.length === 2 && tempRange[1] !== null) {
                    const dayjsEndDate = dayjs(tempRange[1], commonConfig?.dateFormat);
                    const utcOffsetEndDate = dayjs(dayjsEndDate).utcOffset();
                    const endDateUnix = dayjsEndDate.add(utcOffsetEndDate, 'm').utc().startOf('day').unix();
                    // For single select, use the same date for both start and end
                    onDateSubmit(endDateUnix, endDateUnix);
                    setIsOpen(false);
                }
            } else {
                if (tempRange?.length === 2 && !tempRange?.includes(null)) {
                    const dayjsStartDate = dayjs(tempRange[0], commonConfig?.dateFormat);
                    const dayjsEndDate = dayjs(tempRange[1], commonConfig?.dateFormat);
                    const utcOffsetStartDate = dayjs(dayjsStartDate).utcOffset();
                    const utcOffsetEndDate = dayjs(dayjsEndDate).utcOffset();

                    const startDateUnix = dayjsStartDate.add(utcOffsetStartDate, 'm').utc().startOf('day').unix();
                    const endDateUnix = dayjsEndDate.add(utcOffsetEndDate, 'm').utc().startOf('day').unix();
                    onDateSubmit(startDateUnix, endDateUnix);
                    setIsOpen(false);
                }
            }
        };

        // Method to programmatically open the popup
        const openPopup = () => {
            setIsOpen(true);
        };

        // Expose the openPopup method through ref
        useImperativeHandle(ref, () => ({
            openPopup,
        }));

        const disabledDateHandler = (currentDate: Dayjs) => {
            //current date formatted
            const formattedDate = currentDate.startOf('day');
            const disabledStartDate = calenderDatesConfig?.disabledDates?.[0]
                ? dayjs(calenderDatesConfig?.disabledDates?.[0], 'MM/DD/YYYY')
                : dayjs(`01/01/${dayjs().year() - 3}`, 'MM/DD/YYYY');
            const disabledEndDate = calenderDatesConfig?.disabledDates?.[1]
                ? dayjs(calenderDatesConfig?.disabledDates?.[1], 'MM/DD/YYYY')
                : dayjs(`12/31/${dayjs().year() + 2}`, 'MM/DD/YYYY');

            return formattedDate.isBefore(disabledStartDate) || formattedDate.isAfter(disabledEndDate);
        };

        const renderFooter = () => {
            return (
                <div className="text-center py-2">
                    <Button
                        style={{ borderRadius: 4, fontSize: 13, fontWeight: 600 }}
                        type="primary"
                        onClick={onMonthlySubmit}
                        disabled={
                            singleSelect
                                ? tempRange?.length !== 2 || tempRange[1] === null
                                : tempRange?.length !== 2 || tempRange?.includes(null)
                        }
                    >
                        Set Date
                    </Button>
                </div>
            );
        };

        if (singleSelect) {
            const singleDateChange = (date: Dayjs | null) => {
                if (date) {
                    const dayjsEndDate = date.startOf('month');
                    setTempRange([tempRange[0], dayjsEndDate]);
                }
            };

            return (
                <div ref={pickerRef} style={{ width: '100%' }}>
                    <DatePicker
                        disabled={disabled}
                        placement={placement}
                        getPopupContainer={(triggerNode: any) => triggerNode.parentNode}
                        inputReadOnly
                        open={isOpen}
                        defaultPickerValue={dayjs(`01/01/${calenderDatesConfig?.startYear}`)}
                        disabledDate={disabledDateHandler}
                        onOpenChange={handleMonthlyCalenderOpenChange}
                        renderExtraFooter={renderFooter}
                        popupClassName="popup-custom-range-picker"
                        allowClear={false}
                        value={tempRange?.[1] || null}
                        onChange={singleDateChange}
                        format={commonConfig?.dateFormat}
                        className="custom-range-picker custom-range-picker-single"
                        picker="month"
                        placeholder="Enter month"
                        style={{
                            width: '100%',
                            ...(hideInput && {
                                opacity: 0,
                                position: 'absolute',
                                pointerEvents: 'none',
                                width: 0,
                                height: 0,
                                overflow: 'hidden',
                            }),
                        }}
                    />
                </div>
            );
        }

        return (
            <div ref={pickerRef}>
                <RangePicker
                    disabled={disabled}
                    placement={placement}
                    getPopupContainer={(triggerNode: any) => triggerNode.parentNode} // Ensures popup aligns with parent structure
                    inputReadOnly
                    separator={<span className="px-1"> - </span>}
                    open={isOpen}
                    defaultPickerValue={[
                        dayjs(`01/01/${calenderDatesConfig?.startYear}`),
                        dayjs(`01/01/${calenderDatesConfig?.startYear}`),
                    ]}
                    disabledDate={disabledDateHandler}
                    onOpenChange={handleMonthlyCalenderOpenChange}
                    renderExtraFooter={renderFooter}
                    popupClassName="popup-custom-range-picker"
                    allowClear={false}
                    value={tempRange}
                    onCalendarChange={monthlyCalenderChange}
                    format={commonConfig?.dateFormat}
                    className="custom-range-picker"
                    picker="month"
                    style={{
                        ...(hideInput && {
                            opacity: 0,
                            position: 'absolute',
                            pointerEvents: 'none',
                            width: 0,
                            height: 0,
                            overflow: 'hidden',
                        }),
                    }}
                />
            </div>
        );
    },
);

export default MonthlyCalenderRange;
